/*
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved.
* Distributed under the TruVideo proprietary license.
*/
import { useCallback, useEffect, useRef, useState } from 'react';
import apiClient from '../api/client';
import {
  listAudioDevices,
  supportsAudioOutputSelection,
  VoiceAgentClient,
  VoiceAgentConnectError,
} from '../utils/voiceAgentClient';
import {
  VOICE_CLOSE_MESSAGES,
  VOICE_ERR_GENERIC,
  VOICE_ERR_MIC_DENIED,
  VOICE_ERR_NO_MIC,
  VOICE_ERR_TIME_LIMIT,
  VOICE_INPUT_SAMPLE_RATE,
  VOICE_SESSION_MAX_SECONDS,
  voiceAgentUrl,
} from '../constants/voiceAgent';
import {
  DEFAULT_STT_MODEL,
  DEFAULT_TTS_VOICE,
  findSttModel,
  findTtsVoice,
} from '../constants/voiceModels';

/** Server messages forwarded to `handlers` (same names as the protocol). */
const FORWARDED_EVENTS = [
  'session.ready',
  'user.speech.started',
  'user.speech.stopped',
  'transcript.interim',
  'transcript.final',
  'user.turn.ended',
  'agent.response.started',
  'agent.text.delta',
  'agent.action',
  'agent.text.completed',
  'agent.audio.started',
  'agent.audio.completed',
  'agent.interrupted',
  'error',
];

const UNAUTHORIZED = 4401;
/** Thrown inside start() when the attempt was cancelled (stop, unmount, agent change, newer start). */
const CANCELLED = Symbol('voice start cancelled');
const getToken = () => localStorage.getItem('auth_access_token') || '';

// Remembered device choices (per browser; a convenience only).
const INPUT_DEVICE_KEY = 'voiceAgent.inputDeviceId';
const OUTPUT_DEVICE_KEY = 'voiceAgent.outputDeviceId';
// Remembered speech-to-text model and agent voice.
const STT_MODEL_KEY = 'voiceAgent.sttModel';
const TTS_VOICE_KEY = 'voiceAgent.ttsVoice';
const readStored = (key) => {
  try { return localStorage.getItem(key) || ''; } catch { return ''; }
};
const writeStored = (key, value) => {
  try {
    if (value) localStorage.setItem(key, value);
    else localStorage.removeItem(key);
  } catch { /* storage unavailable */ }
};
/** The stored model if it is still in the catalog, else the default. */
const storedSttModel = () => {
  const stored = readStored(STT_MODEL_KEY);
  return findSttModel(stored) ? stored : DEFAULT_STT_MODEL;
};
const storedTtsVoice = () => {
  const stored = readStored(TTS_VOICE_KEY);
  return findTtsVoice(stored) ? stored : DEFAULT_TTS_VOICE;
};
/** The stored id if that device is still present, else '' (system default). */
const validId = (id, list) => (id && list.some((d) => d.deviceId === id) ? id : '');

const errorMessage = (err) => {
  if (err?.name === 'NotAllowedError' || err?.name === 'SecurityError') return VOICE_ERR_MIC_DENIED;
  if (err?.name === 'NotFoundError') return VOICE_ERR_NO_MIC;
  if (err instanceof VoiceAgentConnectError && err.code) return VOICE_CLOSE_MESSAGES[err.code] || err.reason || VOICE_ERR_GENERIC;
  return err?.message || VOICE_ERR_GENERIC;
};

/**
 * React glue for VoiceAgentClient: creates the client on start(), mirrors its
 * state (connection, server state, mic, level) and forwards protocol events
 * to `handlers[eventName]`. Disposes the client on stop(), unmount, or when
 * `sessionMetadata.agentId` changes.
 *
 * Tokens: the WebSocket can't use the axios refresh interceptor, so start()
 * first makes a cheap authenticated API call (GET /auth/me) — an expired
 * access token is refreshed there — and retries once if the server answers 4401.
 *
 * Devices: prepareDevices() asks for mic permission and lists microphones and
 * speakers (labels are hidden before permission); the chosen ids are
 * remembered in localStorage and can be changed mid-session. A device that is
 * the only one of its kind is picked automatically. The speech-to-text model
 * and the agent voice are remembered the same way (defaults:
 * DEFAULT_STT_MODEL / DEFAULT_TTS_VOICE) and apply from the next connection:
 * they are sent in `session.start` (`metadata.sttModel`, `metadata.ttsVoice`).
 * Call primeAudio()
 * synchronously in the click handler so the AudioContext is created inside the
 * user gesture even when a permission prompt or device picker comes first.
 */
export default function useVoiceAgent({ sessionMetadata = {}, handlers = {}, onClosed } = {}) {
  const [status, setStatus] = useState('idle');
  const [agentState, setAgentState] = useState('idle');
  const [micOn, setMicOn] = useState(false);
  const [muted, setMutedState] = useState(false);
  const agentStateRef = useRef('idle');
  const [level, setLevel] = useState(0);
  const [error, setError] = useState(null);
  const [remainingSeconds, setRemainingSeconds] = useState(null);
  const deadlineRef = useRef(null);
  const [devices, setDevices] = useState({ inputs: [], outputs: [] });
  const [inputDeviceId, setInputDeviceIdState] = useState(() => readStored(INPUT_DEVICE_KEY));
  const [outputDeviceId, setOutputDeviceIdState] = useState(() => readStored(OUTPUT_DEVICE_KEY));
  const outputSelectable = supportsAudioOutputSelection();
  const [sttModel, setSttModelState] = useState(storedSttModel);
  const [ttsVoice, setTtsVoiceState] = useState(storedTtsVoice);
  const modelsRef = useRef({ sttModel, ttsVoice });
  modelsRef.current = { sttModel, ttsVoice };
  const clientRef = useRef(null);
  // Bumped by dispose() and each start(); a start() whose number is stale gives up.
  const startGenRef = useRef(0);
  const ctxRef = useRef(null);
  const deviceIdsRef = useRef({ input: inputDeviceId, output: outputDeviceId });
  deviceIdsRef.current = { input: inputDeviceId, output: outputDeviceId };
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;
  const onClosedRef = useRef(onClosed);
  onClosedRef.current = onClosed;
  const metadataRef = useRef(sessionMetadata);
  metadataRef.current = sessionMetadata;

  const dispose = useCallback(async () => {
    startGenRef.current += 1; // cancels a start() still in progress
    const client = clientRef.current;
    clientRef.current = null;
    if (client) await client.disconnect();
    setStatus('idle');
    setAgentState('idle');
    setMicOn(false);
    setMutedState(false);
    setLevel(0);
  }, []);

  const createClient = useCallback(() => {
    const models = modelsRef.current;
    const client = new VoiceAgentClient({
      url: voiceAgentUrl(),
      getToken,
      sessionMetadata: { ...metadataRef.current, sttModel: models.sttModel, ttsVoice: models.ttsVoice },
      inputSampleRate: VOICE_INPUT_SAMPLE_RATE,
      inputDeviceId: deviceIdsRef.current.input,
      outputDeviceId: outputSelectable ? deviceIdsRef.current.output : '',
      audioContext: ctxRef.current || undefined,
    });
    client.on('connection', ({ status: next }) => setStatus(next));
    client.on('session.ready', () => setAgentState('listening'));
    client.on('state.changed', ({ state }) => setAgentState(state));
    client.on('mic', ({ muted: isMuted }) => setMutedState(!!isMuted));
    client.on('mic', ({ on }) => setMicOn(on));
    client.on('level', ({ rms }) => setLevel(rms));
    // The chosen mic disappeared and capture fell back to the system default.
    client.on('devices', ({ fallback }) => {
      if (fallback) { setInputDeviceIdState(''); writeStored(INPUT_DEVICE_KEY, ''); }
    });
    client.on('closed', ({ code, reason }) => {
      if (clientRef.current !== client) return;
      clientRef.current = null;
      setAgentState('idle');
      setMicOn(false);
      setLevel(0);
      if (code !== 1000) {
        const message = VOICE_CLOSE_MESSAGES[code] || reason || VOICE_ERR_GENERIC;
        setError(message);
        onClosedRef.current?.({ code, reason, message });
      }
    });
    FORWARDED_EVENTS.forEach((type) => {
      client.on(type, (message) => handlersRef.current[type]?.(message));
    });
    return client;
  }, [outputSelectable]);

  /** Creates/resumes the shared AudioContext. Call synchronously inside a click handler. */
  const primeAudio = useCallback(() => {
    if (!ctxRef.current && window.AudioContext) {
      ctxRef.current = new window.AudioContext({ latencyHint: 'interactive' });
    }
    if (ctxRef.current?.state === 'suspended') ctxRef.current.resume().catch(() => {});
  }, []);

  const refreshDevices = useCallback(async (requestPermission = false) => {
    const list = await listAudioDevices({ requestPermission });
    setDevices(list);
    // Forget choices whose device is gone; the only device of its kind is picked automatically.
    const only = (l) => (l.length === 1 ? l[0].deviceId : '');
    const input = validId(deviceIdsRef.current.input, list.inputs) || only(list.inputs);
    const output = validId(deviceIdsRef.current.output, list.outputs) || only(list.outputs);
    if (input !== deviceIdsRef.current.input) { setInputDeviceIdState(input); writeStored(INPUT_DEVICE_KEY, input); }
    if (output !== deviceIdsRef.current.output) { setOutputDeviceIdState(output); writeStored(OUTPUT_DEVICE_KEY, output); }
    return list;
  }, []);

  /**
   * Asks for microphone permission (once) and lists the devices. Returns
   * { inputs, outputs, showInput, showOutput } — show* when there is more than
   * one microphone / selectable speaker to choose from. Null when access was refused.
   */
  const prepareDevices = useCallback(async () => {
    setError(null);
    try {
      const list = await refreshDevices(true);
      return {
        ...list,
        showInput: list.inputs.length > 1,
        showOutput: outputSelectable && list.outputs.length > 1,
      };
    } catch (err) {
      setError(errorMessage(err));
      return null;
    }
  }, [refreshDevices, outputSelectable]);

  const setInputDevice = useCallback(async (deviceId) => {
    setInputDeviceIdState(deviceId);
    writeStored(INPUT_DEVICE_KEY, deviceId);
    deviceIdsRef.current = { ...deviceIdsRef.current, input: deviceId };
    try {
      await clientRef.current?.setInputDevice(deviceId);
    } catch (err) {
      setError(errorMessage(err));
    }
  }, []);

  const setOutputDevice = useCallback(async (deviceId) => {
    setOutputDeviceIdState(deviceId);
    writeStored(OUTPUT_DEVICE_KEY, deviceId);
    deviceIdsRef.current = { ...deviceIdsRef.current, output: deviceId };
    try {
      await clientRef.current?.setOutputDevice(deviceId);
    } catch {
      setError('Could not switch to that speaker.');
    }
  }, []);

  // Model choices apply from the next connection (they are sent in session.start).
  const setSttModel = useCallback((model) => {
    if (!findSttModel(model)) return;
    setSttModelState(model);
    writeStored(STT_MODEL_KEY, model);
  }, []);

  const setTtsVoice = useCallback((model) => {
    if (!findTtsVoice(model)) return;
    setTtsVoiceState(model);
    writeStored(TTS_VOICE_KEY, model);
  }, []);

  // Keep the lists current when devices are plugged in or removed (once listed).
  useEffect(() => {
    const mediaDevices = navigator.mediaDevices;
    if (!mediaDevices?.addEventListener || !devices.inputs.length) return undefined;
    const onChange = () => { refreshDevices(false).catch(() => {}); };
    mediaDevices.addEventListener('devicechange', onChange);
    return () => mediaDevices.removeEventListener('devicechange', onChange);
  }, [devices.inputs.length, refreshDevices]);

  /**
   * Connects (if needed) and opens the microphone. Call from a click.
   * stop(), unmount, an agent change or a newer start() cancel it at any await.
   */
  const start = useCallback(async () => {
    const generation = ++startGenRef.current;
    const current = () => startGenRef.current === generation;
    setError(null);
    setAgentState('connecting');
    const attempt = async () => {
      await apiClient.get('/auth/me'); // refreshes an expired access token via the interceptor
      if (!current()) throw CANCELLED;
      const client = createClient();
      clientRef.current = client;
      try {
        await client.connect();
        if (!current()) throw CANCELLED;
        await client.startMicrophone();
        if (!current()) throw CANCELLED;
      } catch (err) {
        if (clientRef.current === client) clientRef.current = null;
        await client.disconnect();
        throw err;
      }
    };
    try {
      if (clientRef.current?.status === 'ready') {
        await clientRef.current.startMicrophone();
        return;
      }
      try {
        await attempt();
      } catch (err) {
        if (!current() || !(err instanceof VoiceAgentConnectError) || err.code !== UNAUTHORIZED) throw err;
        await attempt();
      }
    } catch (err) {
      if (!current()) return; // cancelled: dispose() (or the newer start) owns the state now
      setAgentState('idle');
      setError(errorMessage(err));
    }
  }, [createClient]);

  const stop = useCallback(() => dispose(), [dispose]);
  const interrupt = useCallback(() => clientRef.current?.interrupt(), []);
  const sendText = useCallback((text) => clientRef.current?.sendText(text), []);
  const commit = useCallback(() => clientRef.current?.commit(), []);

  /**
   * Mutes/unmutes the mic for the running session (no audio is sent while
   * muted). Muting mid-sentence commits what was said so far, so the turn is
   * answered instead of waiting for audio that will not come.
   */
  const setMuted = useCallback((next) => {
    const client = clientRef.current;
    if (!client) return;
    if (next && agentStateRef.current === 'user_speaking') client.commit();
    client.setMuted(next);
  }, []);
  const toggleMute = useCallback(() => setMuted(!clientRef.current?.muted), [setMuted]);

  /*
   * Session time limit (VOICE_SESSION_MAX_SECONDS): the countdown starts when
   * the session is ready and ends it at 0. Remaining time is computed from the
   * deadline, so it stays right when a background tab throttles the interval.
   * Restarting the mic or muting keeps the same deadline.
   */
  useEffect(() => {
    if (!VOICE_SESSION_MAX_SECONDS || status !== 'ready') {
      deadlineRef.current = null;
      setRemainingSeconds(null);
      return undefined;
    }
    if (!deadlineRef.current) deadlineRef.current = Date.now() + VOICE_SESSION_MAX_SECONDS * 1000;
    const tick = () => {
      const left = Math.max(0, Math.ceil((deadlineRef.current - Date.now()) / 1000));
      setRemainingSeconds(left);
      if (left === 0) {
        clearInterval(timer);
        dispose().then(() => setError(VOICE_ERR_TIME_LIMIT));
      }
    };
    const timer = setInterval(tick, 1000);
    tick();
    return () => clearInterval(timer);
  }, [status, dispose]);

  // A different agent means a different conversation.
  useEffect(() => {
    dispose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionMetadata.agentId]);
  useEffect(() => () => {
    dispose();
    const ctx = ctxRef.current;
    ctxRef.current = null;
    ctx?.close().catch(() => {});
  }, [dispose]);

  agentStateRef.current = agentState;

  return {
    status,
    agentState,
    micOn,
    muted,
    setMuted,
    toggleMute,
    level,
    error,
    remainingSeconds,
    sessionMaxSeconds: VOICE_SESSION_MAX_SECONDS,
    active: status === 'ready' || status === 'connecting' || status === 'authenticating',
    start,
    stop,
    interrupt,
    sendText,
    commit,
    clearError: () => setError(null),
    devices,
    inputDeviceId,
    outputDeviceId,
    outputSelectable,
    primeAudio,
    prepareDevices,
    setInputDevice,
    setOutputDevice,
    sttModel,
    ttsVoice,
    setSttModel,
    setTtsVoice,
  };
}
